import { Box } from '@mui/material';
import { FormattedDate, FormattedMessage, FormattedTime, IntlShape } from 'react-intl';
import { tokens } from '@synergy/theme-palo';

import { PoolTimelineItem } from '../../../common/components/timeline/models/pool-timeline-item';
import { SentMessage, SentMessageStatus } from '../../../common/models/sent-message';

const c = tokens.color;

export interface MessageTimelineContext {
    intl: IntlShape;
}

/**
 * Maps a sent mail onto a timeline item (#2155).
 *
 * The status is shown rather than assumed: a mail sitting in the queue reads as
 * queued until the mailer says it went out, and a failed one says why.
 */

const STATUS_LOOK: Record<
    SentMessageStatus,
    { variant: PoolTimelineItem['variant']; labelId: string; sentenceId: string }
> = {
    [SentMessageStatus.Queued]: {
        variant: 'warn',
        labelId: 'app.stream.message.queued',
        sentenceId: 'app.stream.sentence.message.queued',
    },
    [SentMessageStatus.Sent]: {
        variant: 'ok',
        labelId: 'app.stream.message.sent',
        sentenceId: 'app.stream.sentence.message.sent',
    },
    [SentMessageStatus.Failed]: {
        variant: 'crit',
        labelId: 'app.stream.message.failed',
        sentenceId: 'app.stream.sentence.message.failed',
    },
};

export const createMessageTimelineItem = (message: SentMessage, _context: MessageTimelineContext): PoolTimelineItem => {
    const look = STATUS_LOOK[message.status] ?? STATUS_LOOK[SentMessageStatus.Queued];
    const shownAt = message.sentAt ?? message.queuedAt;

    return {
        id: message.id,
        key: `message-${message.id}`,
        timestamp: message.queuedAt,
        variant: look.variant,
        icon: SentMessageStatus.Failed === message.status ? 'icon-cancel' : 'icon-message',
        header: message.subject,
        // Who sent it is the system; naming a person here would be a guess.
        sentence: (
            <FormattedMessage
                id={look.sentenceId}
                values={{ subject: message.subject, recipient: message.recipient }}
            />
        ),
        subheader: (
            <span>
                <FormattedMessage id={look.labelId} /> {message.recipient}
                {' · '}
                <FormattedDate value={shownAt} />
                &nbsp;
                <FormattedTime value={shownAt} />
            </span>
        ),
        footer: undefined,
        content: (
            <Box>
                {message.failureReason && <Box sx={{ fontSize: '11px', color: c.crit }}>{message.failureReason}</Box>}
                {message.attachmentNames.length > 0 && (
                    <Box sx={{ fontSize: '10.5px', color: c.ink2 }}>
                        <FormattedMessage id="app.stream.message.attachments" /> {message.attachmentNames.join(', ')}
                    </Box>
                )}
            </Box>
        ),
        commentsDisabled: true,
        comments: [],
        changes: [],
    };
};
